import React, { useState } from 'react';

function usePagination(data, perPage) {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(data.length / perPage) || 1;
  const slice = data.slice((page - 1) * perPage, page * perPage);
  const reset = () => setPage(1);
  return { page, setPage, totalPages, slice, reset };
}

function PaginationBar({ page, totalPages, setPage }) {
  if (totalPages <= 1) return null;
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) pages.push(i);
    else if (pages[pages.length - 1] !== '...') pages.push('...');
  }
  return (
    <div className="flex items-center justify-between mt-2">
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:bg-surface-container disabled:opacity-30 transition-all">
        <span className="material-symbols-outlined text-[14px]">chevron_left</span>Prev
      </button>
      <div className="flex items-center gap-1">
        {pages.map((p, i) => p === '...' ? (
          <span key={`d-${i}`} className="px-1 text-xs text-secondary">…</span>
        ) : (
          <button key={p} onClick={() => setPage(p)}
            className={`w-7 h-7 rounded-lg text-xs font-bold transition-all
              ${page === p ? 'bg-primary text-white' : 'text-secondary hover:bg-surface-container'}`}>
            {p}
          </button>
        ))}
      </div>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:bg-surface-container disabled:opacity-30 transition-all">
        Next<span className="material-symbols-outlined text-[14px]">chevron_right</span>
      </button>
    </div>
  );
}

export default function InquiriesPanel({ inquiries, loading, onReply }) {
  const [replyText,    setReplyText]    = useState({});
  const [replyingTo,   setReplyingTo]   = useState(null);
  const [submittingId, setSubmittingId] = useState(null);
  const [filter,       setFilter]       = useState('all');
  const [perPage,      setPerPage]      = useState(5);

  const handleReply = async (id) => {
    if (!replyText[id]?.trim()) return;
    setSubmittingId(id);
    try {
      await onReply(id, replyText[id]);
      setReplyingTo(null);
      setReplyText(t => ({ ...t, [id]: '' }));
    } finally { setSubmittingId(null); }
  };

  const filteredInquiries = filter === 'all'
    ? inquiries
    : inquiries.filter(q => q.status === filter);

  const pagination = usePagination(filteredInquiries, perPage);

  if (loading) return (
    <div className="py-16 flex justify-center">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4">

      {/* ── Filter tabs ── */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'open', 'replied'].map(s => (
          <button key={s} onClick={() => { setFilter(s); pagination.reset(); }}
            className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
              filter === s
                ? 'bg-primary text-white'
                : s === 'open'
                  ? 'bg-amber-100 text-amber-700'
                  : s === 'replied'
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-gray-100 text-gray-600'
            }`}>
            {s === 'all' ? inquiries.length : inquiries.filter(q => q.status === s).length} {s}
          </button>
        ))}
      </div>

      {filteredInquiries.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-outline-variant/10">
          <span className="material-symbols-outlined text-4xl text-outline-variant">forum</span>
          <p className="mt-3 font-semibold">No inquiries yet</p>
          <p className="text-sm text-secondary mt-1">Buyer messages will appear here</p>
        </div>
      ) : pagination.slice.map(q => (
        <div key={q.id}
          className={`bg-white rounded-2xl shadow-sm border overflow-hidden transition-all ${
            q.status === 'open'
              ? 'border-amber-200'
              : q.status === 'replied'
                ? 'border-emerald-200'
                : 'border-outline-variant/10'
          }`}>

          {/* ── Animal context bar ── */}
          <div className="flex items-center gap-2 px-5 py-2.5 bg-surface-container-low border-b border-outline-variant/10">
            <span className="material-symbols-outlined text-[16px] text-secondary">pets</span>
            <span className="text-xs font-bold text-on-surface">
              {q.animal_name || `Animal #${q.animal_id}`}
            </span>
            <span className="text-[10px] font-mono text-outline bg-surface-container px-2 py-0.5 rounded-full">
              ID #{q.animal_id}
            </span>
            {q.type && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                {q.type}
              </span>
            )}
            <span className="ml-auto text-[10px] text-outline">
              {new Date(q.created_at).toLocaleDateString('en-IN', {
                day: 'numeric', month: 'short', year: 'numeric'
              })}
              {' · '}
              {new Date(q.created_at).toLocaleTimeString('en-IN', {
                hour: '2-digit', minute: '2-digit'
              })}
            </span>
          </div>

          <div className="p-5 space-y-3">
            {/* ── Buyer + message ── */}
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center
                              text-primary font-black text-sm flex-shrink-0">
                {(q.buyer_name || 'B')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-bold text-sm">{q.buyer_name || 'Anonymous Buyer'}</span>
                  {q.buyer_phone && (
                    <a href={`tel:${q.buyer_phone}`}
                      className="text-[10px] text-primary flex items-center gap-0.5 hover:underline">
                      <span className="material-symbols-outlined text-[12px]">call</span>
                      {q.buyer_phone}
                    </a>
                  )}
                  <span className="text-[10px] text-outline font-mono">Inquiry #{q.id}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    q.status === 'replied'
                      ? 'bg-emerald-100 text-emerald-700'
                      : q.status === 'open'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-gray-100 text-gray-500'
                  }`}>{q.status || 'open'}</span>
                </div>
                <p className="text-sm text-on-surface leading-relaxed">{q.message}</p>
              </div>
            </div>

            {/* ── Existing reply ── */}
            {q.reply && (
              <div className="ml-11 flex items-start gap-2 bg-emerald-50 border border-emerald-200
                              rounded-xl px-4 py-3">
                <span className="material-symbols-outlined text-emerald-600 text-[16px] mt-0.5 flex-shrink-0">
                  reply
                </span>
                <div>
                  <p className="text-[10px] font-bold text-emerald-600 mb-0.5">YOUR REPLY</p>
                  <p className="text-sm text-emerald-800">{q.reply}</p>
                </div>
              </div>
            )}

            {/* ── Reply input ── */}
            {replyingTo === q.id && (
              <div className="ml-11 flex gap-2">
                <textarea
                  value={replyText[q.id] || ''} rows={2}
                  onChange={e => setReplyText(t => ({ ...t, [q.id]: e.target.value }))}
                  placeholder="Type your reply to the buyer..."
                  className="flex-1 px-3 py-2 rounded-xl border border-outline-variant/40
                             bg-surface-container-low text-sm outline-none focus:border-primary
                             focus:ring-2 focus:ring-primary/10 resize-none transition-all" />
                <div className="flex flex-col gap-1">
                  <button onClick={() => handleReply(q.id)} disabled={submittingId === q.id}
                    className="px-4 py-2 rounded-xl bg-primary text-white font-bold text-sm
                               hover:opacity-90 transition-all disabled:opacity-60">
                    {submittingId === q.id ? '…' : 'Send'}
                  </button>
                  <button onClick={() => setReplyingTo(null)}
                    className="px-4 py-2 rounded-xl border border-outline-variant/30 text-secondary
                               text-xs font-bold hover:bg-surface-container transition-all">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* ── Action buttons ── */}
            {q.status !== 'replied' && replyingTo !== q.id && (
              <div className="ml-11">
                <button onClick={() => setReplyingTo(q.id)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary
                             text-white text-xs font-bold hover:opacity-90 transition-all">
                  <span className="material-symbols-outlined text-[15px]">reply</span>
                  Reply to Buyer
                </button>
              </div>
            )}
          </div>
        </div>
      ))}

      {/* ── Per page + pagination ── */}
      {filteredInquiries.length > 0 && (
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-1">
            <span className="text-xs text-secondary mr-1">Per page</span>
            {[5, 10, 20].map(n => (
              <button key={n} onClick={() => { setPerPage(n); pagination.reset(); }}
                className={`w-8 h-7 rounded-lg text-xs font-bold transition-all ${
                  perPage === n
                    ? 'bg-primary text-white'
                    : 'text-secondary hover:bg-surface-container border border-surface-container-high'
                }`}>
                {n}
              </button>
            ))}
          </div>
          <p className="text-xs text-secondary">
            {((pagination.page - 1) * perPage) + 1}–{Math.min(pagination.page * perPage, filteredInquiries.length)} of {filteredInquiries.length}
          </p>
        </div>
      )}
      <PaginationBar page={pagination.page} totalPages={pagination.totalPages} setPage={pagination.setPage} />
    </div>
  );
}